export const RecipeType=Object.freeze({
  OFFICIAL:'official',
  COMMUNITY:'community',
  PRIVATE:'private'
});

export const RecipeVisibility=Object.freeze({
  PUBLIC:'public',
  UNLISTED:'unlisted',
  PRIVATE:'private'
});

const TYPES=new Set(Object.values(RecipeType));
const VISIBILITIES=new Set(Object.values(RecipeVisibility));
const LOCALE=/^[a-z]{2,3}(?:-[A-Z]{2})?$/;

function required(value,label,max=160){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalText(value,label,max=4000){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalId(value,label){
  if(value==null||value==='')return null;
  return required(value,label,128);
}

function idOrGenerated(value){
  const id=String(value??'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function recipeType(value){
  const type=String(value??'').trim().toLowerCase();
  if(!TYPES.has(type))throw new TypeError('Unsupported recipe type');
  return type;
}

function visibility(value){
  const key=String(value??'').trim().toLowerCase();
  if(!VISIBILITIES.has(key))throw new TypeError('Unsupported recipe visibility');
  return key;
}

export function recipeLocale(value){
  const raw=String(value??'').trim();
  const parts=raw.replace('_','-').split('-');
  const normalized=parts.length===2
    ?parts[0].toLowerCase()+'-'+parts[1].toUpperCase()
    :parts[0]?.toLowerCase();
  if(!normalized||!LOCALE.test(normalized))throw new TypeError('Invalid recipe locale');
  return normalized;
}

function timestamp(value,label){
  const text=String(value??'').trim();
  if(!text||Number.isNaN(Date.parse(text)))throw new TypeError(label+' must be an ISO timestamp');
  return text;
}

function positiveVersion(value){
  const number=Number(value);
  if(!Number.isInteger(number)||number<1)throw new TypeError('version must be a positive integer');
  return number;
}

function optionalUrl(value){
  if(value==null||value==='')return null;
  let url;
  try{url=new URL(String(value));}catch{throw new TypeError('sourceUrl must be an HTTP(S) URL');}
  if(!['http:','https:'].includes(url.protocol))throw new TypeError('sourceUrl must be an HTTP(S) URL');
  return url.toString();
}

export function createRecipeTranslation({locale,title,description=null}={}){
  return Object.freeze({
    locale:recipeLocale(locale),
    title:required(title,'title',160),
    description:optionalText(description,'description',8000)
  });
}

export function createRecipe({
  id,
  type,
  visibility:recipeVisibility,
  authorUserId=null,
  authorName=null,
  sourceName=null,
  sourceUrl=null,
  defaultLocale='pl',
  translations=[],
  version=1,
  createdAt=new Date().toISOString(),
  updatedAt=createdAt,
  deletedAt=null
}={}){
  const recipeId=idOrGenerated(id);
  const kind=recipeType(type);
  const access=visibility(recipeVisibility);
  const owner=optionalId(authorUserId,'authorUserId');
  if(kind!==RecipeType.OFFICIAL&&!owner)throw new TypeError('authorUserId is required for community/private recipes');
  if(kind===RecipeType.PRIVATE&&access!==RecipeVisibility.PRIVATE){
    throw new TypeError('Private recipes must have private visibility');
  }

  const locale=recipeLocale(defaultLocale);
  if(!Array.isArray(translations)||translations.length<1)throw new TypeError('At least one recipe translation is required');
  const localized=translations.map(createRecipeTranslation);
  if(new Set(localized.map(item=>item.locale)).size!==localized.length){
    throw new TypeError('Recipe translations must use unique locales');
  }
  const primary=localized.find(item=>item.locale===locale);
  if(!primary)throw new TypeError('defaultLocale must have a translation');

  const created=timestamp(createdAt,'createdAt');
  const updated=timestamp(updatedAt,'updatedAt');
  if(Date.parse(updated)<Date.parse(created))throw new TypeError('updatedAt cannot be before createdAt');

  return Object.freeze({
    id:recipeId,
    type:kind,
    visibility:access,
    authorUserId:owner,
    authorName:optionalText(authorName,'authorName',160),
    sourceName:optionalText(sourceName,'sourceName',240),
    sourceUrl:optionalUrl(sourceUrl),
    defaultLocale:locale,
    translations:Object.freeze(localized),
    title:primary.title,
    description:primary.description,
    version:positiveVersion(version),
    createdAt:created,
    updatedAt:updated,
    deletedAt:deletedAt==null||deletedAt===''?null:timestamp(deletedAt,'deletedAt')
  });
}

export function recipeTranslation(recipe,{locale=null}={}){
  const value=createRecipe(recipe);
  const wanted=locale?recipeLocale(locale):value.defaultLocale;
  return value.translations.find(item=>item.locale===wanted)
    ||value.translations.find(item=>item.locale===value.defaultLocale);
}

export function recipeToRows(recipe){
  const value=createRecipe(recipe);
  return Object.freeze({
    recipe:Object.freeze({
      id:value.id,
      recipe_type:value.type,
      visibility:value.visibility,
      author_user_id:value.authorUserId,
      author_name:value.authorName,
      source_name:value.sourceName,
      source_url:value.sourceUrl,
      default_locale:value.defaultLocale,
      version:value.version,
      created_at:value.createdAt,
      updated_at:value.updatedAt,
      deleted_at:value.deletedAt
    }),
    translations:Object.freeze(value.translations.map(item=>Object.freeze({
      recipe_id:value.id,
      locale:item.locale,
      title:item.title,
      description:item.description
    })))
  });
}

export function recipeFromRows(row={},translations=[]){
  return createRecipe({
    id:row.id,
    type:row.recipe_type,
    visibility:row.visibility,
    authorUserId:row.author_user_id,
    authorName:row.author_name,
    sourceName:row.source_name,
    sourceUrl:row.source_url,
    defaultLocale:row.default_locale,
    translations:(translations||[]).map(item=>({
      locale:item.locale,
      title:item.title,
      description:item.description
    })),
    version:row.version,
    createdAt:row.created_at,
    updatedAt:row.updated_at,
    deletedAt:row.deleted_at
  });
}
